import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useGetUpcomingEventsQuery } from "../api/businessApi";

export default function Events() {
  const [activeFilter, setActiveFilter] = useState("all");
  const { data, isLoading, error } = useGetUpcomingEventsQuery({ limit: 50 });

  const events = data?.events || data || [];

  const filteredEvents =
    activeFilter === "all"
      ? events
      : events.filter(
          (event) =>
            event.type?.toLowerCase() === activeFilter.toLowerCase()
        );

  const filterTabs = [
    { key: "all", label: "All" },
    { key: "in-person", label: "In-Person" },
    { key: "virtual", label: "Virtual" },
    { key: "hybrid", label: "Hybrid" },
  ];

  const getTypeBadgeClasses = (type) => {
    switch (type?.toLowerCase()) {
      case "virtual":
        return "bg-blue-100 text-blue-800";
      case "hybrid":
        return "bg-purple-100 text-purple-800";
      case "in-person":
      default:
        return "bg-green-100 text-green-800";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Date TBA";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-[#135879] mb-4">
            Upcoming Events
          </h1>
          <div className="w-20 h-1 bg-[#d4af37] mx-auto mb-4"></div>
          <p className="text-gray-600 max-w-3xl mx-auto mb-8">
            Discover and register for our upcoming community events, webinars,
            and networking opportunities.
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveFilter(tab.key)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                activeFilter === tab.key
                  ? "bg-[#135879] text-white shadow-md"
                  : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-300"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#135879]"></div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-6 text-center">
            <p className="text-red-700">
              Failed to load events. Please try again later.
            </p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && filteredEvents.length === 0 && (
          <div className="text-center py-16">
            <svg
              className="w-16 h-16 mx-auto text-gray-300 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No events found
            </h3>
            <p className="text-gray-500">
              {activeFilter !== "all"
                ? `No ${activeFilter} events are currently scheduled. Try a different filter.`
                : "Check back soon for upcoming events!"}
            </p>
          </div>
        )}

        {/* Events Grid */}
        {!isLoading && !error && filteredEvents.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {filteredEvents.map((event) => (
              <div
                key={event.id}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden"
              >
                {event.image_url && (
                  <img
                    src={event.image_url}
                    alt={event.title}
                    className="w-full h-48 object-cover"
                  />
                )}
                <div className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <Link
                      to={`/events/${event.slug}`}
                      className="text-xl font-bold text-[#135879] hover:text-[#0e4562] transition-colors"
                    >
                      {event.title}
                    </Link>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ml-2 ${getTypeBadgeClasses(
                        event.type
                      )}`}
                    >
                      {event.type || "In-Person"}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4 line-clamp-2">
                    {event.description}
                  </p>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-500">
                      <svg
                        className="w-4 h-4 mr-2 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      {formatDate(event.event_date)}
                      {event.start_time && (
                        <span className="ml-2">
                          at {formatTime(event.start_time)}
                        </span>
                      )}
                    </div>
                    {event.location && (
                      <div className="flex items-center text-sm text-gray-500">
                        <svg
                          className="w-4 h-4 mr-2 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {event.location}
                      </div>
                    )}
                    {event.capacity && (
                      <div className="flex items-center text-sm text-gray-500">
                        <svg
                          className="w-4 h-4 mr-2 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        {event.rsvp_count || 0} / {event.capacity} registered
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <Link
                      to={`/events/${event.slug}`}
                      className="text-[#135879] hover:text-[#0e4562] text-sm font-medium flex items-center"
                    >
                      View Details
                      <svg
                        className="w-4 h-4 ml-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </Link>
                    <Link
                      to={`/events/${event.slug}`}
                      className="bg-[#d4af37] hover:bg-[#c19b2e] text-gray-900 font-semibold py-2 px-4 rounded text-sm transition duration-300"
                    >
                      RSVP Now
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="bg-[#135879] rounded-lg p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">Stay Updated</h3>
          <p className="mb-6 max-w-2xl mx-auto">
            Sign up for our newsletter to receive updates about our upcoming
            events and other exciting news.
          </p>
          <Link
            to="/contact"
            className="bg-[#d4af37] hover:bg-[#c19b2e] text-gray-900 font-semibold py-3 px-8 rounded transition duration-300 inline-block"
          >
            Contact Us for More Info
          </Link>
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/"
            className="inline-flex items-center text-[#135879] hover:text-[#0e4562] font-medium"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
